<template>
  <div class="pdf-actions">
    <!-- Section Selection -->
    <div class="section-selection" v-if="showSectionSelection">
      <h4>Select Report Sections</h4>
      <div class="section-checkboxes">
        <label v-for="section in availableSections" :key="section.key" class="checkbox-label">
          <input
              type="checkbox"
              :value="section.key"
              v-model="selectedSections"
          >
          <span>{{ section.label }}</span>
        </label>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons">
      <md-button
          @click="downloadPdf"
          class="md-primary md-raised"
          :disabled="isLoading || pdfGenerating"
      >
        <md-icon>file_download</md-icon>
        {{ isLoading || pdfGenerating ? 'Generating...' : 'Download PDF' }}
      </md-button>

      <md-button
          @click="previewPdf"
          class="md-info md-raised"
          :disabled="isLoading || pdfGenerating"
      >
        <md-icon>visibility</md-icon>
        Preview
      </md-button>

      <md-button
          @click="regeneratePdf"
          class="md-warning md-raised"
          :disabled="isLoading || pdfGenerating"
          v-if="canRegenerate"
      >
        <md-icon>refresh</md-icon>
        Regenerate
      </md-button>

      <md-button
          @click="printPdf"
          class="md-success md-raised"
          :disabled="isLoading || pdfGenerating"
      >
        <md-icon>print</md-icon>
        Print
      </md-button>
    </div>

    <!-- Progress Indicator -->
    <div v-if="isLoading || pdfGenerating" class="progress-container">
      <md-progress-bar md-mode="indeterminate"></md-progress-bar>
      <p class="progress-text">{{ progressMessage }}</p>
    </div>

    <!-- Error Display -->
    <div v-if="error || pdfError" class="error-message">
      <md-icon>error</md-icon>
      <span>{{ error || pdfError }}</span>
      <md-button class="md-simple md-danger" @click="clearError">Dismiss</md-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Swal from 'sweetalert2';

export default {
  name: 'GuidancePdfActions',
  props: {
    guidanceId: {
      type: [Number, String],
      required: true
    },
    guidanceNumber: {
      type: String,
      default: ''
    },
    canRegenerate: {
      type: Boolean,
      default: false
    },
    showSectionSelection: {
      type: Boolean,
      default: true
    },
    defaultSections: {
      type: Array,
      default: () => [
        'summary',
        'day_type_breakdown',
        'work_points',
        'schedules',
        'exceptions',
        'status_history',
        'takeon_report'
      ]
    }
  },
  data() {
    return {
      isLoading: false,
      progressMessage: 'Preparing PDF...',
      error: null,
      selectedSections: [...this.defaultSections],
      availableSections: [
        { key: 'summary', label: 'Summary' },
        { key: 'day_type_breakdown', label: 'Day Type Breakdown' },
        { key: 'work_points', label: 'Work Points' },
        { key: 'schedules', label: 'Schedules' },
        { key: 'exceptions', label: 'Exceptions' },
        { key: 'status_history', label: 'Status History' },
        { key: 'takeon_report', label: 'Takeon Report' }
      ]
    };
  },
  computed: {
    ...mapState('guidances', ['pdfGenerating']),
    ...mapGetters('guidances', ['pdfPreview', 'pdfDownload', 'pdfError'])
  },
  methods: {
    ...mapActions('guidances', [
      'pdfDownloadAction',
      'pdfPreviewAction',
      'pdfRegenerateAction',
      'clearPdfData'
    ]),

    clearError() {
      this.error = null;
      this.clearPdfData();
    },

    async downloadPdf() {
      if (!this.guidanceId) {
        this.error = 'Guidance data not loaded.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.progressMessage = 'Generating PDF...';

      try {
        // Dispatch action with guidanceId and sections
        const response = await this.pdfDownloadAction({
          guidanceId: this.guidanceId,
          sections: this.selectedSections
        });

        // The response should be the full axios response with blob data
        // Create download link from response data
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `Guidance-${this.guidanceNumber || this.guidanceId}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        await this.$store.dispatch('alerts/success', 'PDF downloaded successfully.');
      } catch (error) {
        this.error = error.message || 'Failed to download PDF. Please try again.';
        await this.$store.dispatch('alerts/error', this.error);
        console.error('PDF download error:', error);
      } finally {
        this.isLoading = false;
        this.progressMessage = 'Preparing PDF...';
      }
    },

    async previewPdf() {
      if (!this.guidanceId) {
        this.error = 'Guidance data not loaded.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.progressMessage = 'Loading preview...';

      try {
        // Dispatch action with guidanceId and sections
        const response = await this.pdfPreviewAction({
          guidanceId: this.guidanceId,
          sections: this.selectedSections
        });

        // Create blob URL from response data
        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/pdf'
        }));
        window.open(url, '_blank');
      } catch (error) {
        this.error = error.message || 'Failed to load PDF preview.';
        await this.$store.dispatch('alerts/error', this.error);
        console.error('PDF preview error:', error);
      } finally {
        this.isLoading = false;
        this.progressMessage = 'Preparing PDF...';
      }
    },

    async regeneratePdf() {
      if (!this.guidanceId) {
        this.error = 'Guidance data not loaded.';
        return;
      }

      // Confirm before regenerating
      const result = await Swal.fire({
        title: 'Regenerate PDF?',
        text: 'This will create a fresh PDF with the latest data. Existing PDF will be replaced.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ff9800',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, regenerate!'
      });

      if (!result.isConfirmed) return;

      this.isLoading = true;
      this.error = null;
      this.progressMessage = 'Regenerating PDF...';

      try {
        await this.pdfRegenerateAction(this.guidanceId);

        await Swal.fire({
          title: 'Success!',
          text: 'PDF has been regenerated successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        await this.$store.dispatch('alerts/success', 'PDF regenerated successfully.');

        // Optionally download after regeneration
        await this.downloadPdf();
      } catch (error) {
        this.error = error.message || 'Failed to regenerate PDF.';
        await this.$store.dispatch('alerts/error', this.error);
        console.error('PDF regeneration error:', error);
      } finally {
        this.isLoading = false;
        this.progressMessage = 'Preparing PDF...';
      }
    },

    async printPdf() {
      if (!this.guidanceId) {
        this.error = 'Guidance data not loaded.';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.progressMessage = 'Loading for print...';

      try {
        // Dispatch action with guidanceId and sections
        const response = await this.pdfPreviewAction({
          guidanceId: this.guidanceId,
          sections: this.selectedSections
        });

        // Create blob URL from response data
        const url = window.URL.createObjectURL(new Blob([response.data], {
          type: 'application/pdf'
        }));

        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.addEventListener('load', () => {
            setTimeout(() => {
              printWindow.print();
            }, 1000);
          });
        }
      } catch (error) {
        this.error = error.message || 'Failed to load PDF for printing.';
        await this.$store.dispatch('alerts/error', this.error);
        console.error('PDF print error:', error);
      } finally {
        this.isLoading = false;
        this.progressMessage = 'Preparing PDF...';
      }
    }
  }
};
</script>

<style scoped>
.pdf-actions {
  padding: 15px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.section-selection {
  margin-bottom: 15px;
  padding: 10px;
  background: white;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
}

.section-selection h4 {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #333;
}

.section-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  cursor: pointer;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.action-buttons .md-button {
  min-width: 120px;
}

.progress-container {
  margin: 10px 0;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 4px;
}

.progress-text {
  margin: 8px 0 0;
  font-size: 13px;
  color: #666;
  text-align: center;
}

.error-message {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  margin-top: 10px;
}

.error-message md-icon {
  color: #c62828;
}
</style>